// AboutScreen.jsx
import React, { Fragment } from 'react';
import { Card, Col, Row, Typography } from 'antd';
import { Heart, People, Password } from 'react-iconly';
import SEO from '../components/SEO/SEO';
import goalImg from '../assets/images/our-goal.png';
import nikoletaImg from '../assets/images/nikoleta.png';
import { SUPPORT_EMAIL, FACEBOOK_URL } from '../constants';
import { FacebookIcon } from '../components/Icons/FacebookIcon';
import SupportPrompt from '../components/Misc/SupportPrompt';
import { LinkedinIcon } from '../components/Icons/LinkedinIcon';

const { Title, Paragraph } = Typography;

// Add new collaborators here as objects in this array.
// To add a photo, paste the image URL into the `photo` field (e.g. your LinkedIn profile photo URL).
const TEAM = [
  {
    initials: 'NM',
    photo: nikoletaImg,
    name: 'Nikoleta Margariti',
    bio: 'I\'ve always been curious about anything metaphysical, but I work in tech and I tend to think in data, so individual anecdotal accounts were never quite enough for me. I started NDE Archive because I wanted to actually research this properly, with all the available data in one place and in a structured, searchable format.\n\nI\'m particularly interested in negative and distressing NDEs. My feeling is that they\'re significantly underreported, mostly because people are afraid or embarrassed to talk about them. I want to understand how common they really are, and whether they tend to shift into something more positive over time.\n\nI\'m also interested in NDE reports from minority communities. Religious fear around concepts like hell can make it even harder for people from certain backgrounds to talk openly about their experiences. I think having real accounts available might genuinely help people feel less alone and more at peace with the topic.\n\nMy hope is that researchers will use this data to find some real answers.',
    linkedin: 'https://www.linkedin.com/in/nikolmarg/',
    facebook: 'https://www.facebook.com/nikoleta.margariti'
  }
];

const iconCircleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.12), rgba(191, 193, 255, 0.12))',
  marginBottom: 16
};

const avatarStyle = {
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #9195fc, #bfc1ff)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  fontWeight: 700,
  color: '#fff',
  flexShrink: 0
};

const AboutScreen = () => {
  return (
    <Fragment>
      <SEO
        title="About | Our Mission & Team"
        description="Learn about NDE Archive's mission and the people behind it: building the largest freely accessible near-death experience database to advance scientific understanding of NDEs."
        path="/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About | NDE Archive',
            description: 'NDE Archive is a free, open platform dedicated to advancing the scientific and human understanding of near-death experiences through structured data, computational analysis, and anonymous contribution.',
            url: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/about`,
            mainEntity: {
              '@type': 'Organization',
              name: 'NDE Archive',
              url: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'NDE Archive',
            url: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com',
            description: 'A free, open-source platform providing a searchable database of near-death experiences (NDEs) with data-driven categorization, Greyson scale scoring, and anonymous contribution.',
            ...(SUPPORT_EMAIL && { email: SUPPORT_EMAIL }),
            ...(FACEBOOK_URL && { sameAs: [FACEBOOK_URL] }),
            knowsAbout: [
              'Near-death experiences',
              'Greyson NDE Scale',
              'Out-of-body experiences',
              'Life review',
              'Afterlife research',
              'Clinical death studies',
              'Distressing near-death experiences'
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com' },
              { '@type': 'ListItem', position: 2, name: 'About', item: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/about` }
            ]
          }
        ]}
      />

      {/* ── Hero ── */}
      <Row justify="center" align="middle" className="mb-32">
        <Col xs={24} md={10}>
          <img
            src={goalImg}
            alt="NDE Archive"
            style={{ maxWidth: '100%', height: 'auto', filter: 'drop-shadow(0 8px 30px rgba(0,0,0,0.08))' }}
          />
        </Col>

        <Col xs={24} md={14}>
          <p style={{ color: '#9195fc', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 13, marginBottom: 12 }}>
            Near-Death Experience Database
          </p>
          <Title level={1} style={{ color: '#1a1a2e', fontWeight: 800, marginBottom: 24, letterSpacing: '-0.5px' }}>About the Project</Title>
          <Paragraph style={{ color: '#555', fontSize: 18, lineHeight: '30px', marginBottom: 16 }}>
            NDE Archive exists to <strong>shed light on one of humanity's greatest mysteries</strong>: what happens at the edge of life. By gathering verified near-death experience accounts from across the web and processing them with <strong>data science and natural language understanding</strong>, the platform turns thousands of individual stories into structured, searchable data.
          </Paragraph>
          <Paragraph style={{ color: '#555', fontSize: 18, lineHeight: '30px', marginBottom: 0 }}>
            The goal is to give researchers, clinicians, and the genuinely curious a <strong>rigorous foundation</strong> for understanding NDEs. Not just the serene, light-filled kind, but the full spectrum, including the distressing experiences that rarely get discussed.
          </Paragraph>
        </Col>
      </Row>

      {/* ── What the platform does ── */}
      <Row gutter={[32, 32]} align="stretch" justify="center" className="mb-64">
        <Col sm={24} md={8}>
          <Card hoverable className="h-100 elevation text-center hover-elevation-2" bordered={false} style={{ borderRadius: 16, overflow: 'hidden' }}>
            <div style={iconCircleStyle}>
              <Password set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} className="mb-24">Data-Driven Insights</Title>
            <Paragraph style={{ color: '#555', fontSize: 16, lineHeight: '26px' }}>
              Advanced <strong>natural language processing and data analysis</strong> convert raw narrative accounts into structured, analysable data, enabling meaningful statistics on incidence rates, emotional tone, Greyson scale scores, and much more.
              <br /><br />
              The result is a <strong>research-ready dataset</strong> that no individual reader could assemble alone.
            </Paragraph>
          </Card>
        </Col>

        <Col sm={24} md={8}>
          <Card hoverable className="h-100 elevation text-center hover-elevation-2" bordered={false} style={{ borderRadius: 16, overflow: 'hidden' }}>
            <div style={iconCircleStyle}>
              <Heart set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} className="mb-24">Negative NDEs Matter</Title>
            <Paragraph style={{ color: '#555', fontSize: 16, lineHeight: '26px' }}>
              Distressing near-death experiences are <strong>significantly underreported</strong>. Fear, shame, and stigma keep many survivors silent. This platform creates a safe, anonymous space where every type of experience can be shared honestly.
              <br /><br />
              Understanding the <strong>dark side of NDEs</strong>, and whether and how they transform into something positive, is just as important as documenting the transcendent.
            </Paragraph>
          </Card>
        </Col>

        <Col sm={24} md={8}>
          <Card hoverable className="h-100 elevation text-center hover-elevation-2" bordered={false} style={{ borderRadius: 16, overflow: 'hidden' }}>
            <div style={iconCircleStyle}>
              <People set="bulk" className="text-primary" size={32} />
            </div>
            <Title level={3} className="mb-24">Open for Research</Title>
            <Paragraph style={{ color: '#555', fontSize: 16, lineHeight: '26px' }}>
              All collected data is available to <strong>researchers and academics</strong>. The platform is built on the conviction that the more rigorously this data is studied, the closer we get to understanding what actually happens at the threshold between life and death.
              <br /><br />
              Curious researchers are warmly <strong>encouraged to reach out</strong> and put this dataset to work.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      {/* ── Team ── */}
      <div className="mb-64">
        <Title
          level={2}
          style={{ color: '#1a1a2e', fontWeight: 800, marginBottom: 8, letterSpacing: '-0.3px', textAlign: 'center' }}
        >
          The People Behind It
        </Title>
        <p style={{ color: '#888', textAlign: 'center', fontSize: 16, marginBottom: 48 }}>
          Built with curiosity and a genuine desire to understand.
        </p>

        <Row gutter={[32, 32]} justify="center">
          {TEAM.map((person) => (
            <Col key={person.name} xs={24}>
              <Card
                bordered={false}
                style={{ borderRadius: 20 }}
                className="elevation"
              >
                <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {person.photo
                    ? <img src={person.photo} alt={person.name} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={avatarStyle}>{person.initials}</div>
                  }

                  <div style={{ flex: 1, minWidth: 200 }}>
                    <Title level={4} style={{ margin: '0 0 4px', color: '#1a1a2e', fontWeight: 700 }}>
                      {person.name}
                    </Title>
                    <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '27px', marginBottom: 16, whiteSpace: 'pre-line' }}>
                      {person.bio}
                    </Paragraph>
                    {(person.linkedin || person.facebook) && (
                      <div style={{ display: 'flex', gap: 10 }}>
                        {person.linkedin && (
                          <a href={person.linkedin} target="_blank" rel="noopener noreferrer" title="LinkedIn" style={{ color: '#9195fc', display: 'inline-flex' }}>
                            <LinkedinIcon style={{ fontSize: 20 }} />
                          </a>
                        )}
                        {person.facebook && (
                          <a href={person.facebook} target="_blank" rel="noopener noreferrer" title="Facebook" style={{ color: '#9195fc', display: 'inline-flex' }}>
                            <FacebookIcon style={{ fontSize: 20 }} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* ── Researcher CTA ── */}
      <Row justify="center" className="mb-64">
        <Col xs={24} md={20} lg={16}>
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.08), rgba(191, 193, 255, 0.14))',
              borderRadius: 20,
              padding: '52px 40px',
              textAlign: 'center'
            }}
          >
            <Title level={3} style={{ color: '#1a1a2e', fontWeight: 700, marginBottom: 12 }}>
              Are You a Researcher?
            </Title>
            <Paragraph style={{ color: '#555', fontSize: 16, lineHeight: '28px', maxWidth: 560, margin: '0 auto 28px' }}>
              The NDE Archive dataset is available for academic and scientific use. If you're studying near-death experiences, especially distressing ones, feel free to get in touch. I'd love to see this data used to find some real answers.
            </Paragraph>
            {SUPPORT_EMAIL && (
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                style={{
                  display: 'inline-block',
                  background: '#9195fc',
                  color: '#fff',
                  padding: '13px 32px',
                  borderRadius: 10,
                  fontWeight: 600,
                  fontSize: 15,
                  textDecoration: 'none'
                }}
              >
                Get in touch
              </a>
            )}
          </div>
        </Col>
      </Row>

      <SupportPrompt variant="about" style={{ margin: '0 auto 64px' }} />
    </Fragment>
  );
};

export default AboutScreen;
